import React from 'react';
import { ArrowUpRight, MessageSquare, Users, Package, Database, Zap, Settings, GitBranch, Phone } from 'lucide-react';
import project1 from '../assets/project_1.png';
import project2 from '../assets/project_2.png';
import project3 from '../assets/project_3.png';
import project4 from '../assets/project_4.png';
import project5 from '../assets/project_5.png';
import project6 from '../assets/project_6.png';
import project7 from '../assets/project_7.png';
import project8 from '../assets/project_8.png';

const cases = [
    {
        title: 'Automated WhatsApp Broadcast System',
        description: 'A system that scrapes daily news, summarizes it using GPT-4, and broadcasts updates to 5,000+ subscribers via WhatsApp API.',
        tags: ['WhatsApp API', 'OpenAI', 'Python'],
        color: 'from-green-500/20 to-emerald-500/5',
        icon: MessageSquare,
        image: project1,
    },
    {
        title: 'Lead Capture & CRM Sync',
        description: 'Unified lead ingestion from Facebook Ads, Website, and LinkedIn into HubSpot, with automatic lead scoring and sales team notification.',
        tags: ['HubSpot', 'Zapier', 'Facebook Ads'],
        color: 'from-orange-500/20 to-red-500/5',
        icon: Users,
        image: project2,
    },
    {
        title: 'E-commerce Inventory Sync',
        description: 'Real-time 2-way sync between Shopify store and physical warehouse inventory, reducing stockouts by 95%.',
        tags: ['Shopify', 'n8n', 'PostgreSQL'],
        color: 'from-blue-500/20 to-indigo-500/5',
        icon: Package,
        image: project3,
    },
    {
        title: 'CRM-MLS Integration Pipeline',
        description: 'Automated real estate data synchronization between CRM systems and Multiple Listing Services, reducing manual data entry by 95%',
        tags: ['n8n', 'Salesforce', 'MLS'],
        color: 'from-purple-500/20 to-pink-500/5',
        icon: Database,
        image: project4,
    },
    {
        title: 'AI-Powered WhatsApp Business Automation',
        description: 'Dedicated 24/7 WhatsApp AI bots that handle customer orders, inquiries, and support requests with intelligent conversation flows and seamless handoff to human agents when needed',
        tags: ['n8n', 'OpenAI API', 'WhatsApp Business'],
        color: 'from-cyan-500/20 to-blue-500/5',
        icon: Zap,
        image: project5,
    },
    {
        title: 'Human-Like AI Call Agent System',
        description: 'Advanced AI call agent that sounds 100% human, automatically picks up calls, handles customer inquiries, takes orders, and provides support with natural conversation capabilities',
        tags: ['Voice AI', 'n8n', 'VoIP Systems'],
        color: 'from-indigo-500/20 to-purple-500/5',
        icon: Phone,
        image: project6,
    },
    {
        title: 'DMS Workflow Automation',
        description: 'Document Management System automation for processing, categorizing, and routing documents based on content and metadata',
        tags: ['Make', 'OCR APIs', 'SharePoint'],
        color: 'from-amber-500/20 to-orange-500/5',
        icon: GitBranch,
        image: project7,
    },
    {
        title: 'Multi-Platform Lead Distribution',
        description: 'Intelligent lead routing system that distributes prospects across multiple sales channels based on criteria and availability',
        tags: ['Zapier', 'HubSpot', 'Pipedrive'],
        color: 'from-rose-500/20 to-red-500/5',
        icon: Settings,
        image: project8,
    },
];

const CaseStudies: React.FC = () => {
    return (
        <section id="work" className="py-24 bg-navy-900">
            <div className="max-w-7xl mx-auto px-6">
                <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
                    <div>
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                            Recent Automations
                        </h2>
                        <p className="text-text-muted max-w-xl">
                            See how we've helped other businesses save time and increase revenue.
                        </p>
                    </div>
                    <a href="#contact" className="text-brand-cyan font-semibold flex items-center gap-2 hover:gap-3 transition-all">
                        View all projects <ArrowUpRight className="w-4 h-4" />
                    </a>
                </div>

                <div className="grid md:grid-cols-3 gap-8">
                    {cases.map((item, index) => (
                        <div
                            key={index}
                            className="group relative rounded-2xl border border-white/[0.05] bg-navy-800 overflow-hidden hover:border-white/[0.1] transition-all"
                        >
                            <div className={`h-48 bg-gradient-to-br ${item.color} p-8 flex flex-col justify-end relative overflow-hidden`}>
                                {item.image ? (
                                    <img
                                        src={item.image}
                                        alt={item.title}
                                        className="absolute inset-0 w-full h-full object-cover opacity-80"
                                    />
                                ) : (
                                    <div className="w-16 h-16 rounded-xl bg-white/10 backdrop-blur-sm grid place-items-center mb-4 relative z-10">
                                        <item.icon className="w-8 h-8 text-white" strokeWidth={1.5} />
                                    </div>
                                )}
                            </div>

                            <div className="p-8">
                                <div className="flex flex-wrap gap-2 mb-4">
                                    {item.tags.map((tag) => (
                                        <span key={tag} className="text-xs font-medium px-2.5 py-1 rounded-full bg-white/[0.05] text-text-muted border border-white/[0.05]">
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-brand-cyan transition-colors">
                                    {item.title}
                                </h3>
                                <p className="text-text-muted text-sm leading-relaxed">
                                    {item.description}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default CaseStudies;
